import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  X, Sparkles, Save, Loader2, Image as ImageIcon, Trash2, Upload,
  Wand2, Wrench, ClipboardList, MessageSquare, Bot, ScrollText, Settings2,
  AlertTriangle, LayoutList, Rows3, Plus, Pencil, CalendarCheck, Copy, ExternalLink,
} from 'lucide-react';
import HighlightTextarea from '../common/HighlightTextarea';
import WhatsAppPreview from './WhatsAppPreview';
import { picturesApi, botBuilderApi, interactivesApi, bookingLinkApi } from '../../services/api';
import './BotSettingsModal.css';

// Small draggable palette of the store's pictures. Dragging a chip onto a
// highlight-textarea inserts its `#pictureN` token.
const PicturePalette = ({ pictures, t }) => {
  if (!pictures || pictures.length === 0) {
    return <p className="bsm-palette-empty">{t('botSettings.noPicturesYet', 'No pictures yet — add some in the Pictures tab.')}</p>;
  }
  return (
    <div className="bsm-palette">
      {pictures.map((p) => (
        <div
          key={p.id}
          className="bsm-chip"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', `#picture${p.number}`);
            e.dataTransfer.effectAllowed = 'copy';
          }}
          title={p.name}
        >
          <img src={p.image_url} alt="" />
          <span className="bsm-chip-token">#picture{p.number}</span>
          <span className="bsm-chip-name">{p.name}</span>
        </div>
      ))}
    </div>
  );
};

// Draggable palette of the store's interactive messages. Dragging a chip onto a
// highlight-textarea inserts its `#options{n}` / `#list{n}` token.
const InteractivePalette = ({ interactives, t }) => {
  if (!interactives || interactives.length === 0) {
    return <p className="bsm-palette-empty">{t('botSettings.noInteractivesYet', 'No interactive messages yet — add some in the Interactive tab.')}</p>;
  }
  return (
    <div className="bsm-palette">
      {interactives.map((it) => (
        <div
          key={it.id}
          className="bsm-chip bsm-chip-interactive"
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/plain', it.token);
            e.dataTransfer.effectAllowed = 'copy';
          }}
          title={it.name}
        >
          {it.kind === 'list' ? <LayoutList size={14} /> : <Rows3 size={14} />}
          <span className="bsm-chip-token">{it.token}</span>
          <span className="bsm-chip-name">{it.name}</span>
        </div>
      ))}
    </div>
  );
};

const BookingLinkPalette = ({ bookingLink, t }) => {
  if (!bookingLink?.is_published) {
    return <p className="bsm-palette-empty">{t('botSettings.bookingLinkDraft', 'Publish your booking page before using it in prompts.')}</p>;
  }
  return (
    <div className="bsm-palette">
      <div
        className="bsm-chip bsm-chip-interactive"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('text/plain', '#bookingLink');
          e.dataTransfer.effectAllowed = 'copy';
        }}
      >
        <CalendarCheck size={14} />
        <span className="bsm-chip-token">#bookingLink</span>
        <span className="bsm-chip-name">{t('botSettings.bookingLink', 'Appointment link')}</span>
      </div>
    </div>
  );
};

const BotSettingsModal = ({
  onClose,
  isRTL,
  theme,
  // From SettingsPage:
  prompts,
  handlePromptChange,
  setPrompts,
  savePrompts,
  savingPrompts,
  botConfig,
  handleBotConfigChange,
  saveBotConfig,
  savingBotConfig,
  botLanguageOptions,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('personality');

  // Unsaved-changes guard. Snapshot the settings as they were when the modal
  // opened so we can diff against edits and warn before closing.
  const [showUnsaved, setShowUnsaved] = useState(false);
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    initialRef.current = { prompts: { ...prompts }, botConfig: { ...botConfig } };
  }

  // Build-it-for-me
  const [showBuilder, setShowBuilder] = useState(false);
  const [builderText, setBuilderText] = useState('');
  const [building, setBuilding] = useState(false);

  // Tweak-it (editing agent)
  const [showTweaker, setShowTweaker] = useState(false);
  const [tweakText, setTweakText] = useState('');
  const [tweaking, setTweaking] = useState(false);
  const [previewMessages, setPreviewMessages] = useState([]);

  // Pictures
  const [pictures, setPictures] = useState([]);
  const [loadingPics, setLoadingPics] = useState(false);
  const [picName, setPicName] = useState('');
  const [picFile, setPicFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Interactive messages (options / list)
  const [interactives, setInteractives] = useState([]);
  const [loadingInteractives, setLoadingInteractives] = useState(false);
  const [savingInteractive, setSavingInteractive] = useState(false);
  const emptyInteractive = { id: null, name: '', kind: 'options', body_text: '', button_text: '', items: [{ title: '', description: '' }] };
  const [intDraft, setIntDraft] = useState(emptyInteractive);
  const [bookingLink, setBookingLink] = useState(null);

  // Lock body scroll while modal open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const fetchPictures = useCallback(async () => {
    setLoadingPics(true);
    try {
      const res = await picturesApi.list();
      if (res.success) setPictures(res.data || []);
    } catch (e) {
      console.error('Failed to load pictures', e);
    } finally {
      setLoadingPics(false);
    }
  }, []);

  useEffect(() => { fetchPictures(); }, [fetchPictures]);

  const fetchInteractives = useCallback(async () => {
    setLoadingInteractives(true);
    try {
      const res = await interactivesApi.list();
      if (res.success) setInteractives(res.data || []);
    } catch (e) {
      console.error('Failed to load interactives', e);
    } finally {
      setLoadingInteractives(false);
    }
  }, []);

  useEffect(() => { fetchInteractives(); }, [fetchInteractives]);

  useEffect(() => {
    bookingLinkApi.get().then((res) => {
      if (res.success) setBookingLink(res.data);
    }).catch(() => {});
  }, []);

  const itemCap = intDraft.kind === 'list' ? 10 : 3;

  const setDraftKind = (kind) => {
    setIntDraft((d) => {
      const cap = kind === 'list' ? 10 : 3;
      return { ...d, kind, items: d.items.slice(0, cap) };
    });
  };

  const updateDraftItem = (idx, field, value) => {
    setIntDraft((d) => {
      const items = d.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it));
      return { ...d, items };
    });
  };

  const addDraftItem = () => {
    setIntDraft((d) => (d.items.length >= (d.kind === 'list' ? 10 : 3)
      ? d
      : { ...d, items: [...d.items, { title: '', description: '' }] }));
  };

  const removeDraftItem = (idx) => {
    setIntDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
  };

  const editInteractive = (it) => {
    setIntDraft({
      id: it.id,
      name: it.name || '',
      kind: it.kind || 'options',
      body_text: it.body_text || '',
      button_text: it.button_text || '',
      items: (it.items && it.items.length ? it.items : [{ title: '', description: '' }]).map((x) => ({
        title: x.title || '',
        description: x.description || '',
      })),
    });
  };

  const resetIntDraft = () => setIntDraft(emptyInteractive);

  const saveInteractive = async () => {
    if (savingInteractive) return;
    const name = intDraft.name.trim();
    const items = intDraft.items
      .map((it) => ({ title: (it.title || '').trim(), description: (it.description || '').trim() }))
      .filter((it) => it.title);
    if (!name) { toast.error(t('botSettings.interactiveNameRequired', 'Give it a name')); return; }
    if (items.length === 0) { toast.error(t('botSettings.interactiveItemsRequired', 'Add at least one option')); return; }
    setSavingInteractive(true);
    try {
      const payload = {
        name,
        kind: intDraft.kind,
        body_text: intDraft.body_text.trim(),
        button_text: intDraft.kind === 'list' ? intDraft.button_text.trim() : '',
        items,
      };
      const res = intDraft.id
        ? await interactivesApi.update(intDraft.id, payload)
        : await interactivesApi.create(payload);
      if (res.success) {
        toast.success(intDraft.id
          ? t('botSettings.interactiveUpdated', 'Interactive updated')
          : t('botSettings.interactiveAdded', 'Interactive added'));
        resetIntDraft();
        fetchInteractives();
      } else {
        toast.error(res.error || t('botSettings.interactiveSaveFailed', 'Could not save'));
      }
    } catch (e) {
      toast.error(e?.message || t('botSettings.interactiveSaveFailed', 'Could not save'));
    } finally {
      setSavingInteractive(false);
    }
  };

  const deleteInteractive = async (it) => {
    if (!window.confirm(t('botSettings.deleteInteractiveConfirm', 'Delete this interactive message?'))) return;
    try {
      const res = await interactivesApi.delete(it.id);
      if (res.success) {
        if (intDraft.id === it.id) resetIntDraft();
        fetchInteractives();
      }
    } catch (e) {
      toast.error(t('botSettings.deleteFailed', 'Delete failed'));
    }
  };

  const openBuilder = () => { setShowTweaker(false); setShowBuilder((s) => !s); };
  const openTweaker = () => { setShowBuilder(false); setShowTweaker((s) => !s); };

  // Pull the last 10 preview-chat messages into the tweak box so the agent knows
  // what went wrong.
  const copyLastMessages = () => {
    const last = (previewMessages || []).slice(-10);
    if (last.length === 0) {
      toast(t('botSettings.noChatYet', 'No chat messages to copy yet — try the bot on the right first.'));
      return;
    }
    const transcript = last
      .map((m) => `${m.role === 'user' ? '🧑' : '🤖'} ${m.content || ''}`)
      .join('\n');
    const header = t('botSettings.transcriptHeader', 'Recent chat:');
    setTweakText((prev) => (prev ? `${prev}\n\n${header}\n${transcript}` : `${header}\n${transcript}`));
    toast.success(t('botSettings.messagesCopied', 'Last messages added'));
  };

  const runTweaker = async () => {
    if (!tweakText.trim() || tweaking) return;
    setTweaking(true);
    try {
      const current = {
        business_name: prompts.business_name,
        business_type: prompts.business_type,
        identity_prompt: prompts.identity_prompt,
        opening_message: prompts.opening_message,
        custom_rules: prompts.custom_rules,
      };
      const res = await botBuilderApi.tweak(tweakText.trim(), current, botConfig.language);
      if (res.success && res.data) {
        setPrompts((prev) => ({
          ...prev,
          business_name: res.data.business_name ?? prev.business_name,
          business_type: res.data.business_type ?? prev.business_type,
          identity_prompt: res.data.identity_prompt ?? prev.identity_prompt,
          opening_message: res.data.opening_message ?? prev.opening_message,
          custom_rules: res.data.custom_rules ?? prev.custom_rules,
        }));
        setShowTweaker(false);
        setTweakText('');
        setActiveTab('personality');
        toast.success(t('botSettings.tweakDone', 'Tweaked — review and save'));
      } else {
        toast.error(res.error || t('botSettings.tweakFailed', 'Could not tweak. Try again.'));
      }
    } catch (e) {
      toast.error(t('botSettings.tweakFailed', 'Could not tweak. Try again.'));
    } finally {
      setTweaking(false);
    }
  };

  const runBuilder = async () => {
    if (!builderText.trim() || building) return;
    setBuilding(true);
    try {
      const res = await botBuilderApi.generate(builderText.trim(), botConfig.language);
      if (res.success && res.data) {
        setPrompts((prev) => ({
          ...prev,
          business_name: res.data.business_name || prev.business_name,
          business_type: res.data.business_type || prev.business_type,
          identity_prompt: res.data.identity_prompt || prev.identity_prompt,
          opening_message: res.data.opening_message || prev.opening_message,
          custom_rules: res.data.custom_rules || prev.custom_rules,
        }));
        setShowBuilder(false);
        setBuilderText('');
        setActiveTab('personality');
        toast.success(t('botSettings.builderDone', 'Draft ready — review and save'));
      } else {
        toast.error(res.error || t('botSettings.builderFailed', 'Could not generate. Try again.'));
      }
    } catch (e) {
      toast.error(t('botSettings.builderFailed', 'Could not generate. Try again.'));
    } finally {
      setBuilding(false);
    }
  };

  const handleUpload = async () => {
    if (!picFile || !picName.trim() || uploading) return;
    setUploading(true);
    try {
      const res = await picturesApi.upload(picFile, picName.trim());
      if (res.success) {
        toast.success(t('botSettings.pictureAdded', 'Picture added'));
        setPicName('');
        setPicFile(null);
        if (fileRef.current) fileRef.current.value = '';
        fetchPictures();
      } else {
        toast.error(res.error || t('botSettings.uploadFailed', 'Upload failed'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || t('botSettings.uploadFailed', 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const renamePicture = async (pic) => {
    const next = window.prompt(t('botSettings.renamePrompt', 'New name for this picture:'), pic.name);
    if (!next || next.trim() === pic.name) return;
    try {
      const res = await picturesApi.rename(pic.id, next.trim());
      if (res.success) {
        fetchPictures();
      } else {
        toast.error(res.error || t('botSettings.renameFailed', 'Rename failed'));
      }
    } catch (e) {
      toast.error(t('botSettings.renameFailed', 'Rename failed'));
    }
  };

  const deletePicture = async (pic) => {
    if (!window.confirm(t('botSettings.deletePictureConfirm', 'Delete this picture?'))) return;
    try {
      const res = await picturesApi.delete(pic.id);
      if (res.success) fetchPictures();
    } catch (e) {
      toast.error(t('botSettings.deleteFailed', 'Delete failed'));
    }
  };

  // Overrides handed to the live preview (uses unsaved edits).
  const overrides = {
    business_name: prompts.business_name,
    identity_prompt: prompts.identity_prompt,
    custom_rules: prompts.custom_rules,
    opening_message: prompts.opening_message,
    language: botConfig.language,
    max_future_appointments: botConfig.max_future_appointments,
    ai_appointments_enabled: botConfig.ai_appointments_enabled,
    ai_products_enabled: botConfig.ai_products_enabled,
  };

  const saveAll = async () => {
    const okPrompts = await savePrompts();
    const okConfig = await saveBotConfig();
    const ok = okPrompts !== false && okConfig !== false;
    if (ok) {
      // Freshly-saved values become the new baseline so closing won't re-prompt.
      initialRef.current = { prompts: { ...prompts }, botConfig: { ...botConfig } };
      toast.success(t('settings.savedSuccessfully'));
    } else {
      toast.error(t('botSettings.saveFailed', 'Could not save. Please try again.'));
    }
    return ok;
  };

  // ─── Unsaved-changes guard ───
  const PROMPT_KEYS = ['business_name', 'business_type', 'identity_prompt', 'opening_message', 'custom_rules'];
  const CONFIG_KEYS = ['language', 'max_future_appointments', 'ai_appointments_enabled', 'ai_products_enabled', 'lead_summary_enabled', 'lead_summary_language'];

  const isDirty = () => {
    const init = initialRef.current;
    if (!init) return false;
    const pDirty = PROMPT_KEYS.some((k) => (prompts?.[k] ?? '') !== (init.prompts?.[k] ?? ''));
    const cDirty = CONFIG_KEYS.some((k) => (botConfig?.[k] ?? '') !== (init.botConfig?.[k] ?? ''));
    return pDirty || cDirty;
  };

  // All close paths (overlay, X, footer Close) route through here.
  const requestClose = () => {
    if (isDirty()) setShowUnsaved(true);
    else onClose();
  };

  const discardAndClose = () => {
    // Revert the parent's live state back to the opening snapshot.
    const init = initialRef.current;
    if (init) {
      if (typeof setPrompts === 'function') {
        setPrompts((prev) => ({ ...prev, ...init.prompts }));
      }
      Object.entries(init.botConfig).forEach(([k, v]) => handleBotConfigChange(k, v));
    }
    setShowUnsaved(false);
    onClose();
  };

  const saveAndClose = async () => {
    await saveAll();
    setShowUnsaved(false);
    onClose();
  };

  const savingAny = savingPrompts || savingBotConfig;

  const tabs = [
    { id: 'personality', label: t('botSettings.tabPersonality', 'Personality'), icon: Bot },
    { id: 'rules', label: t('botSettings.tabRules', 'Rules'), icon: ScrollText },
    { id: 'pictures', label: t('botSettings.tabPictures', 'Pictures'), icon: ImageIcon },
    { id: 'interactive', label: t('botSettings.tabInteractive', 'Interactive'), icon: LayoutList },
    { id: 'bookingLink', label: t('botSettings.bookingLink', 'Appointment link'), icon: CalendarCheck },
    { id: 'behavior', label: t('botSettings.tabBehavior', 'Behavior'), icon: Settings2 },
  ];

  return (
    <div className="bsm-overlay" onClick={requestClose}>
      <div className={`bsm-modal ${isRTL ? 'rtl' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bsm-header">
          <div className="bsm-header-title">
            <Bot size={20} />
            <h2>{t('botSettings.title', 'Bot Settings')}</h2>
          </div>
          <div className="bsm-header-actions">
            <button className="bsm-build-btn" onClick={openBuilder}>
              <Wand2 size={16} />
              {t('botSettings.buildForMe', 'Build it for me')}
            </button>
            <button className="bsm-tweak-btn" onClick={openTweaker}>
              <Wrench size={16} />
              {t('botSettings.tweakIt', 'Tweak it')}
            </button>
            <button className="bsm-icon-btn" onClick={requestClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Build-it-for-me panel */}
        {showBuilder && (
          <div className="bsm-builder">
            <div className="bsm-builder-head">
              <Sparkles size={16} />
              <span>{t('botSettings.builderTitle', 'Describe your business and let AI set everything up')}</span>
            </div>
            <textarea
              className="input textarea bsm-builder-input"
              rows={3}
              placeholder={t('botSettings.builderPlaceholder', 'e.g. I run a barber shop in Haifa, open Sun–Thu 9–7, we do fades, beard trims and kids cuts. Friendly and quick tone.')}
              value={builderText}
              onChange={(e) => setBuilderText(e.target.value)}
            />
            <div className="bsm-builder-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowBuilder(false)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button className="btn btn-primary btn-sm" onClick={runBuilder} disabled={building || !builderText.trim()}>
                {building ? <Loader2 size={16} className="spinner" /> : <Sparkles size={16} />}
                {building ? t('botSettings.building', 'Building…') : t('botSettings.generate', 'Generate')}
              </button>
            </div>
          </div>
        )}

        {/* Tweak-it agent panel */}
        {showTweaker && (
          <div className="bsm-builder bsm-tweaker">
            <div className="bsm-builder-head bsm-tweaker-head">
              <Wrench size={16} />
              <span>{t('botSettings.tweakTitle', 'Tell the agent what to change — it edits your current setup')}</span>
            </div>
            <textarea
              className="input textarea bsm-builder-input"
              rows={3}
              placeholder={t('botSettings.tweakPlaceholder', "e.g. Make the tone more formal, and don't promise same-day appointments. The bot keeps mixing up prices — fix that.")}
              value={tweakText}
              onChange={(e) => setTweakText(e.target.value)}
            />
            <div className="bsm-builder-actions">
              <button className="btn btn-secondary btn-sm bsm-copy-btn" onClick={copyLastMessages}>
                <ClipboardList size={16} />
                {t('botSettings.copyLastMessages', 'Copy last 10 messages')}
              </button>
              <div className="bsm-builder-actions-end">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowTweaker(false)}>
                  {t('common.cancel', 'Cancel')}
                </button>
                <button className="btn btn-primary btn-sm" onClick={runTweaker} disabled={tweaking || !tweakText.trim()}>
                  {tweaking ? <Loader2 size={16} className="spinner" /> : <Wrench size={16} />}
                  {tweaking ? t('botSettings.tweaking', 'Tweaking…') : t('botSettings.applyTweak', 'Apply tweak')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Body: form + live preview */}
        <div className="bsm-body">
          <div className="bsm-form">
            {/* Tabs */}
            <div className="bsm-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`bsm-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="bsm-tab-content">
              {activeTab === 'personality' && (
                <>
                  <div className="bsm-field">
                    <label>{t('settings.businessName')}</label>
                    <input
                      type="text"
                      className="input"
                      placeholder={t('settings.businessNamePlaceholder')}
                      value={prompts.business_name}
                      onChange={(e) => handlePromptChange('business_name', e.target.value)}
                    />
                  </div>
                  <div className="bsm-field">
                    <label>{t('settings.businessType')}</label>
                    <input
                      type="text"
                      className="input"
                      placeholder={t('settings.businessTypePlaceholder')}
                      value={prompts.business_type}
                      onChange={(e) => handlePromptChange('business_type', e.target.value)}
                    />
                  </div>
                  <div className="bsm-field">
                    <label>{t('settings.identityPrompt')}</label>
                    <HighlightTextarea
                      value={prompts.identity_prompt}
                      onChange={(v) => handlePromptChange('identity_prompt', v)}
                      placeholder={t('settings.identityPromptPlaceholder')}
                      rows={4}
                    />
                    <span className="bsm-hint">{t('settings.identityPromptHint')}</span>
                  </div>
                  <div className="bsm-field">
                    <label><MessageSquare size={14} /> {t('settings.openingMessage')}</label>
                    <span className="bsm-hint bsm-hint-top">{t('botSettings.dragHintOpening', 'Drag a picture or interactive message below to include it in the greeting.')}</span>
                    {(pictures.length > 0 || interactives.length > 0 || bookingLink?.is_published) && (
                      <div className="bsm-palette-stack">
                        {pictures.length > 0 && <PicturePalette pictures={pictures} t={t} />}
                        {interactives.length > 0 && <InteractivePalette interactives={interactives} t={t} />}
                        {bookingLink?.is_published && <BookingLinkPalette bookingLink={bookingLink} t={t} />}
                      </div>
                    )}
                    <HighlightTextarea
                      value={prompts.opening_message}
                      onChange={(v) => handlePromptChange('opening_message', v)}
                      placeholder={t('settings.openingMessagePlaceholder')}
                      rows={3}
                    />
                    <span className="bsm-hint">{t('settings.openingMessageHint')}</span>
                  </div>
                </>
              )}

              {activeTab === 'rules' && (
                <>
                  <div className="bsm-field">
                    <label>{t('settings.customInstructions')}</label>
                    <span className="bsm-hint bsm-hint-top">{t('botSettings.dragHintRules', 'Drag a picture or interactive message below into the text to reference it.')}</span>
                    <div className="bsm-palette-stack">
                      <PicturePalette pictures={pictures} t={t} />
                      <InteractivePalette interactives={interactives} t={t} />
                      <BookingLinkPalette bookingLink={bookingLink} t={t} />
                    </div>
                    <HighlightTextarea
                      value={prompts.custom_rules}
                      onChange={(v) => handlePromptChange('custom_rules', v)}
                      placeholder={t('settings.customInstructionsPlaceholder')}
                      rows={10}
                    />
                    <span className="bsm-hint">{t('settings.customInstructionsHint')}</span>
                  </div>
                </>
              )}

              {activeTab === 'pictures' && (
                <div className="bsm-pictures">
                  <div className="bsm-upload-card">
                    <div className="bsm-upload-row">
                      <input
                        type="text"
                        className="input"
                        placeholder={t('botSettings.pictureNamePlaceholder', 'Picture name (e.g. fade example)')}
                        value={picName}
                        onChange={(e) => setPicName(e.target.value)}
                      />
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={(e) => setPicFile(e.target.files?.[0] || null)}
                        className="bsm-file-input"
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleUpload}
                        disabled={uploading || !picFile || !picName.trim()}
                      >
                        {uploading ? <Loader2 size={16} className="spinner" /> : <Upload size={16} />}
                        {t('botSettings.addPicture', 'Add picture')}
                      </button>
                    </div>
                    {picFile && <span className="bsm-file-name">{picFile.name}</span>}
                  </div>

                  {loadingPics ? (
                    <div className="bsm-loading"><Loader2 className="spinner" size={22} /></div>
                  ) : pictures.length === 0 ? (
                    <p className="bsm-empty">{t('botSettings.noPictures', 'No pictures uploaded yet.')}</p>
                  ) : (
                    <div className="bsm-pic-grid">
                      {pictures.map((p) => (
                        <div
                          key={p.id}
                          className="bsm-pic-card"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', `#picture${p.number}`);
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                        >
                          <img src={p.image_url} alt={p.name} />
                          <div className="bsm-pic-meta">
                            <span className="bsm-pic-token">#picture{p.number}</span>
                            <span className="bsm-pic-name" onClick={() => renamePicture(p)} title={t('botSettings.clickToRename', 'Click to rename')}>
                              {p.name}
                            </span>
                          </div>
                          <button className="bsm-pic-del" onClick={() => deletePicture(p)} aria-label="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'interactive' && (
                <div className="bsm-interactive">
                  {/* Existing interactive messages */}
                  {loadingInteractives ? (
                    <div className="bsm-loading"><Loader2 className="spinner" size={22} /></div>
                  ) : interactives.length === 0 ? (
                    <p className="bsm-empty">{t('botSettings.noInteractives', 'No interactive messages yet. Create one below.')}</p>
                  ) : (
                    <div className="bsm-int-list">
                      {interactives.map((it) => (
                        <div key={it.id} className={`bsm-int-card ${intDraft.id === it.id ? 'active' : ''}`}>
                          <div className="bsm-int-card-head">
                            {it.kind === 'list' ? <LayoutList size={16} /> : <Rows3 size={16} />}
                            <span className="bsm-int-token">{it.token}</span>
                            <span className="bsm-int-name">{it.name}</span>
                            <span className="bsm-int-kind">{it.kind === 'list'
                              ? t('botSettings.kindList', 'List')
                              : t('botSettings.kindOptions', 'Buttons')}</span>
                            <div className="bsm-int-card-actions">
                              <button className="bsm-icon-btn-sm" onClick={() => editInteractive(it)} aria-label="Edit"><Pencil size={14} /></button>
                              <button className="bsm-icon-btn-sm" onClick={() => deleteInteractive(it)} aria-label="Delete"><Trash2 size={14} /></button>
                            </div>
                          </div>
                          <div className="bsm-int-card-items">
                            {(it.items || []).map((o, oi) => (
                              <span key={oi} className="bsm-int-pill">{o.title}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Editor */}
                  <div className="bsm-int-editor">
                    <div className="bsm-int-editor-head">
                      <h4>{intDraft.id
                        ? t('botSettings.editInteractive', 'Edit interactive message')
                        : t('botSettings.newInteractive', 'New interactive message')}</h4>
                      {intDraft.id && (
                        <button className="btn btn-secondary btn-sm" onClick={resetIntDraft}>
                          {t('botSettings.newInteractiveBtn', 'New')}
                        </button>
                      )}
                    </div>

                    {/* Kind selector */}
                    <div className="bsm-int-kind-switch">
                      <button
                        type="button"
                        className={`bsm-int-kind-opt ${intDraft.kind === 'options' ? 'active' : ''}`}
                        onClick={() => setDraftKind('options')}
                      >
                        <Rows3 size={16} />
                        <div>
                          <strong>{t('botSettings.kindOptions', 'Buttons')}</strong>
                          <span>{t('botSettings.kindOptionsHint', 'Up to 3 quick-reply buttons')}</span>
                        </div>
                      </button>
                      <button
                        type="button"
                        className={`bsm-int-kind-opt ${intDraft.kind === 'list' ? 'active' : ''}`}
                        onClick={() => setDraftKind('list')}
                      >
                        <LayoutList size={16} />
                        <div>
                          <strong>{t('botSettings.kindList', 'List')}</strong>
                          <span>{t('botSettings.kindListHint', 'A tappable menu, up to 10 items')}</span>
                        </div>
                      </button>
                    </div>

                    <div className="bsm-field">
                      <label>{t('botSettings.interactiveName', 'Name (used for the token & AI context)')}</label>
                      <input
                        type="text"
                        className="input"
                        placeholder={t('botSettings.interactiveNamePlaceholder', 'e.g. talk to a human')}
                        value={intDraft.name}
                        onChange={(e) => setIntDraft((d) => ({ ...d, name: e.target.value }))}
                      />
                    </div>

                    <div className="bsm-field">
                      <label>{t('botSettings.interactiveBody', 'Message / question shown with it')}</label>
                      <textarea
                        className="input textarea"
                        rows={2}
                        placeholder={t('botSettings.interactiveBodyPlaceholder', 'e.g. How can we help you?')}
                        value={intDraft.body_text}
                        onChange={(e) => setIntDraft((d) => ({ ...d, body_text: e.target.value }))}
                      />
                    </div>

                    {intDraft.kind === 'list' && (
                      <div className="bsm-field">
                        <label>{t('botSettings.interactiveButtonText', 'List button label')}</label>
                        <input
                          type="text"
                          className="input"
                          placeholder={t('botSettings.interactiveButtonPlaceholder', 'e.g. Choose an option')}
                          value={intDraft.button_text}
                          onChange={(e) => setIntDraft((d) => ({ ...d, button_text: e.target.value }))}
                        />
                      </div>
                    )}

                    <div className="bsm-field">
                      <label>{t('botSettings.interactiveItems', 'Options')} ({intDraft.items.length}/{itemCap})</label>
                      <div className="bsm-int-items">
                        {intDraft.items.map((it, idx) => (
                          <div key={idx} className="bsm-int-item-row">
                            <input
                              type="text"
                              className="input bsm-int-item-title"
                              placeholder={t('botSettings.interactiveItemTitle', 'Option label')}
                              value={it.title}
                              maxLength={24}
                              onChange={(e) => updateDraftItem(idx, 'title', e.target.value)}
                            />
                            {intDraft.kind === 'list' && (
                              <input
                                type="text"
                                className="input bsm-int-item-desc"
                                placeholder={t('botSettings.interactiveItemDesc', 'Description (optional)')}
                                value={it.description}
                                maxLength={72}
                                onChange={(e) => updateDraftItem(idx, 'description', e.target.value)}
                              />
                            )}
                            <button
                              className="bsm-icon-btn-sm"
                              onClick={() => removeDraftItem(idx)}
                              disabled={intDraft.items.length <= 1}
                              aria-label="Remove"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                      {intDraft.items.length < itemCap && (
                        <button className="btn btn-secondary btn-sm bsm-int-add" onClick={addDraftItem}>
                          <Plus size={14} /> {t('botSettings.addOption', 'Add option')}
                        </button>
                      )}
                    </div>

                    <div className="bsm-int-editor-actions">
                      <button className="btn btn-primary btn-sm" onClick={saveInteractive} disabled={savingInteractive}>
                        {savingInteractive ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
                        {intDraft.id ? t('common.update', 'Update') : t('common.create', 'Create')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'bookingLink' && (
                <div className="bsm-booking-link">
                  <div className="bsm-booking-hero">
                    <span><CalendarCheck size={25} /></span>
                    <div>
                      <h3>{t('botSettings.bookingLinkTitle', 'Send customers to your booking page')}</h3>
                      <p>{t('botSettings.bookingLinkHint', 'Drag the token into the opening message or rules. The bot only knows about the link where you reference it.')}</p>
                    </div>
                  </div>
                  <BookingLinkPalette bookingLink={bookingLink} t={t} />
                  {bookingLink?.booking_url && (
                    <div className="bsm-booking-url">
                      <code>{bookingLink.booking_url}</code>
                      <button onClick={() => { navigator.clipboard.writeText(bookingLink.booking_url); toast.success(t('common.copied', 'Copied')); }}><Copy size={15} /></button>
                      <a href={bookingLink.booking_url} target="_blank" rel="noreferrer"><ExternalLink size={15} /></a>
                    </div>
                  )}
                  <div className="bsm-booking-example">
                    <b>{t('botSettings.bookingLinkExample', 'Example rule')}</b>
                    <p>{t('botSettings.bookingLinkExampleText', 'When the customer wants to schedule, explain that booking is completed online and send #bookingLink')}</p>
                    <small>{t('botSettings.bookingLinkTypeHint', 'Optional: use #bookingLink{service name} to open the page with a service preselected.')}</small>
                  </div>
                  <button className="btn btn-secondary" onClick={() => { window.location.hash = '#/booking-link'; }}>
                    <CalendarCheck size={16} />{t('botSettings.configureBookingLink', 'Configure booking page')}
                  </button>
                </div>
              )}

              {activeTab === 'behavior' && (
                <>
                  <div className="bsm-toggle-row">
                    <div>
                      <h4>{t('settings.aiAppointments', 'AI books appointments')}</h4>
                      <p>{t('settings.aiAppointmentsHint')}</p>
                    </div>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={botConfig.ai_appointments_enabled}
                        onChange={(e) => handleBotConfigChange('ai_appointments_enabled', e.target.checked)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                  <div className="bsm-toggle-row">
                    <div>
                      <h4>{t('settings.aiProducts', 'AI shows products')}</h4>
                      <p>{t('settings.aiProductsHint')}</p>
                    </div>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={botConfig.ai_products_enabled}
                        onChange={(e) => handleBotConfigChange('ai_products_enabled', e.target.checked)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                  <div className="bsm-field">
                    <label>{t('settings.botLanguage')}</label>
                    <select
                      className="input"
                      value={botConfig.language}
                      onChange={(e) => handleBotConfigChange('language', e.target.value)}
                    >
                      {botLanguageOptions.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bsm-field">
                    <label>{t('settings.maxFutureAppointments')}</label>
                    <select
                      className="input"
                      value={botConfig.max_future_appointments}
                      onChange={(e) => handleBotConfigChange('max_future_appointments', parseInt(e.target.value))}
                    >
                      {[1, 2, 3, 4, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="bsm-toggle-row">
                    <div>
                      <h4>{t('settings.leadSummary', 'WhatsApp chat summary')}</h4>
                      <p>{t('settings.leadSummaryHint')}</p>
                    </div>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={botConfig.lead_summary_enabled}
                        onChange={(e) => handleBotConfigChange('lead_summary_enabled', e.target.checked)}
                      />
                      <span className="switch-slider"></span>
                    </label>
                  </div>
                  {botConfig.lead_summary_enabled && (
                    <div className="bsm-field">
                      <label>{t('settings.leadSummaryLanguage', 'Summary language')}</label>
                      <select
                        className="input"
                        value={botConfig.lead_summary_language}
                        onChange={(e) => handleBotConfigChange('lead_summary_language', e.target.value)}
                      >
                        {botLanguageOptions.map((l) => (
                          <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Live preview */}
          <div className="bsm-preview-pane">
            <WhatsAppPreview
              overrides={overrides}
              businessName={prompts.business_name}
              isRTL={isRTL}
              theme={theme === 'light' ? 'light' : 'dark'}
              onMessagesChange={setPreviewMessages}
              pictures={pictures}
              interactives={interactives}
              bookingLink={bookingLink}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bsm-footer">
          <button className="btn btn-secondary" onClick={requestClose}>{t('common.close', 'Close')}</button>
          <button className="btn btn-primary" onClick={saveAll} disabled={savingAny}>
            {savingAny ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
            {savingAny ? t('settings.saving') : t('common.save', 'Save')}
          </button>
        </div>

        {/* Unsaved-changes guard: fires when closing with pending edits. */}
        {showUnsaved && (
          <div className="bsm-confirm-overlay" onClick={() => setShowUnsaved(false)}>
            <div className="bsm-confirm" onClick={(e) => e.stopPropagation()}>
              <div className="bsm-confirm-icon"><AlertTriangle size={22} /></div>
              <h3 className="bsm-confirm-title">{t('botSettings.unsavedTitle', 'Save changes?')}</h3>
              <p className="bsm-confirm-message">
                {t('botSettings.unsavedMessage', 'You have unsaved changes to your bot settings. Do you want to save them before closing?')}
              </p>
              <div className="bsm-confirm-actions">
                <button className="btn btn-secondary btn-sm" onClick={discardAndClose}>
                  {t('botSettings.discard', 'Discard')}
                </button>
                <button className="btn btn-primary btn-sm" onClick={saveAndClose} disabled={savingAny}>
                  {savingAny ? <Loader2 size={16} className="spinner" /> : <Save size={16} />}
                  {savingAny ? t('settings.saving') : t('common.save', 'Save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BotSettingsModal;
